import { hmacSha256 } from "./hmac-sha256.js";
/** HMAC-SHA-256 output, bytes — the `code_digest` column's `octet_length`. */
export const KEYED_DIGEST_BYTE = 32;
/**
 * Digits in an emailed sign-in code. Engineering, not commercial: the length people already type from an
 * authenticator app, and under the owner's attempt cap (`signInCodeAttemptMaximum`, 5) a guesser's chance per
 * challenge is 5 in 10^6.
 */
export const SIGN_IN_CODE_DIGIT = 6;
/** A pepper is 32 random bytes as hex (the RB-11 provisioning shape); anything much shorter is a defect. */
export const PEPPER_MIN_LENGTH = 32;
/**
 * How a numeric code's first digit is drawn (plan-029 D-8): `any` (0–9, the sign-in code's) or `non_zero` (1–9 — the
 * caller confirmation code's, which removes the dropped-leading-zero failure of a spoken or keyed code at the cost of
 * 10 % of the space: 900,000 six-digit codes).
 */
export const NUMERIC_CODE_FIRST_DIGIT = ["any", "non_zero"];
/**
 * The lengths a party's keyed code may have (ED-11): the confirmation code (6, `CONFIRMATION_CODE_DIGIT`) and a passcode
 * (4–8, `passcode_digit_count`, 0187's CHECK). The confirmation code and passcode digests refuse any other length.
 */
export const PARTY_KEYED_CODE_DIGIT_RANGE = Object.freeze({ least: 4, most: 8 });
const textEncoder = new TextEncoder();
/**
 * The pepper's bytes, after checking it looks like the provisioned secret. Raises naming the SECRET NAME, never
 * echoing the value.
 *
 * The bytes are the secret's UTF-8 text, exactly what `createHmac("sha256", pepperString)` keys with — so a digest
 * made here equals one the operator tree has always made from the same string.
 */
export function pepperBytesOf(secretValue, secretName) {
    if (typeof secretValue !== "string" || secretValue.length < PEPPER_MIN_LENGTH) {
        throw new RangeError(`the pepper ${secretName} must be at least ${String(PEPPER_MIN_LENGTH)} characters; ` +
            "resolve it from Key Vault by name at start-up");
    }
    return textEncoder.encode(secretValue);
}
/** HMAC-SHA-256 of `value` (UTF-8) under `pepper` — design §27.10.3's `keyedDigest`. */
export function keyedDigest(pepper, value) {
    if (pepper.length === 0)
        throw new RangeError("a keyed digest needs a non-empty pepper");
    return hmacSha256(pepper, textEncoder.encode(value));
}
/** Constant-time equality of two digests — design §27.10.3's `digestEqual`. Different lengths are unequal. */
export function digestEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let difference = 0;
    for (let index = 0; index < a.length; index += 1)
        difference |= a[index] ^ b[index];
    return difference === 0;
}
/**
 * Mints a numeric code: `digit` decimal digits (1–12), uniform — the first one over 1–9 when `option.firstDigit` is
 * `non_zero`.
 *
 * Each digit is drawn from one random byte by REJECTION, never `byte % 10`: 256 is not a multiple of 10, so the
 * modulo would make 0–5 slightly likelier than 6–9. Bytes 250–255 are discarded and redrawn. A non-zero first digit is
 * `1 + byte % 9` over bytes 0–251 (252 = 9 × 28), bytes 252–255 redrawn. With `any` (the default) the bytes are read
 * exactly as before the lift, so a scripted source mints the same code it always did.
 */
export function mintNumericCode(random, digit = SIGN_IN_CODE_DIGIT, option = { firstDigit: "any" }) {
    if (!Number.isSafeInteger(digit) || digit < 1 || digit > 12)
        throw new RangeError("a numeric code has 1 to 12 digits");
    if (!NUMERIC_CODE_FIRST_DIGIT.includes(option.firstDigit))
        throw new RangeError("a numeric code's first digit is any or non_zero");
    const nonZeroFirst = option.firstDigit === "non_zero";
    let code = "";
    while (code.length < digit) {
        const bytes = random.fill(new Uint8Array(digit));
        for (const byte of bytes) {
            if (code.length === digit)
                continue;
            if (code.length === 0 && nonZeroFirst) {
                if (byte >= 252)
                    continue;
                code += String(1 + (byte % 9));
                continue;
            }
            if (byte >= 250)
                continue;
            code += String(byte % 10);
        }
    }
    return code;
}
/**
 * What a person typed, as the code it means: spaces and hyphens removed ("123 456", "123-456"). Returns null when
 * what is left is not exactly `digit` decimal digits — the caller still spends an attempt and still compares
 * against a digest, so a malformed code costs what a wrong one costs.
 */
export function presentedNumericCode(raw, digit = SIGN_IN_CODE_DIGIT) {
    const code = raw.replace(/[\s-]/g, "");
    return new RegExp(`^\\d{${String(digit)}}$`).test(code) ? code : null;
}
/**
 * The digest stored for (and compared against) one sign-in challenge's code. The message is
 * `sign-in-code \0 <challenge id> \0 <code>`: the label keeps these digests apart from any other use of the same
 * pepper, and the NUL separators cannot occur in a UUID or a digit string, so no two (id, code) pairs collide.
 */
export function signInCodeDigestOf(pepper, signInCodeChallengeId, code) {
    if (signInCodeChallengeId.length === 0)
        throw new RangeError("a sign-in code digest needs its challenge id");
    return keyedDigest(pepper, `sign-in-code\u0000${signInCodeChallengeId}\u0000${code}`);
}
/**
 * Checks a presented sign-in code against the stored digest, in constant time. A malformed presentation is
 * compared as a placeholder of the right shape, so it costs exactly what a wrong code costs and never matches.
 */
export function signInCodeMatches(pepper, signInCodeChallengeId, presented, storedDigest) {
    const code = presentedNumericCode(presented);
    const candidate = signInCodeDigestOf(pepper, signInCodeChallengeId, code === null ? "x".repeat(SIGN_IN_CODE_DIGIT) : code);
    return digestEqual(candidate, storedDigest) && code !== null;
}
// ---- the party's keyed codes (plan-029 KEYED, design §40.9.2–§40.9.4) ---------------------------------------------------
/** A party keyed code: 4–8 decimal digits. A programming error otherwise — the capture window hands over exact runs only. */
function assertPartyKeyedCode(code, what) {
    const { least, most } = PARTY_KEYED_CODE_DIGIT_RANGE;
    if (!new RegExp(`^\\d{${String(least)},${String(most)}}$`).test(code)) {
        throw new RangeError(`${what} is ${String(least)} to ${String(most)} decimal digits`);
    }
}
function assertIdPart(value, what) {
    if (value.length === 0)
        throw new RangeError(`${what} is required for a keyed code digest`);
    if (value.includes("\u0000"))
        throw new RangeError(`${what} must not hold a NUL character`);
}
/**
 * The digest the runtime keeps for (and compares against) one confirmation code (design §40.9.2): the message is
 * `confirmation-code \0 <conversation_id> \0 <ordinal> \0 <code>`, under `confirmation-code-pepper`. Bound to the
 * conversation AND the code's ordinal, so the same six digits sent twice — to one conversation or to two — are different
 * digests, and a code is only ever compared against its own record. The pepper never enters Redis: the comparison is the
 * runtime's, through {@link digestEqual}.
 */
export function confirmationCodeDigestOf(pepper, conversationId, ordinal, code) {
    assertIdPart(conversationId, "the conversation id");
    if (!Number.isSafeInteger(ordinal) || ordinal < 1)
        throw new RangeError("a confirmation code's ordinal is a positive integer");
    assertPartyKeyedCode(code, "a confirmation code");
    return keyedDigest(pepper, `confirmation-code\u0000${conversationId}\u0000${String(ordinal)}\u0000${code}`);
}
/**
 * The digest stored on an access entry for its passcode (`party_access_entry.passcode_digest`, design §40.9.4): the
 * message is `party-passcode \0 <party_access_entry_id> \0 <passcode>`, under `party-passcode-pepper` — the api writes
 * it, the runtime compares a presented passcode against it. Bound to the entry, so two entries with the same passcode
 * store different digests.
 */
export function passcodeDigestOf(pepper, partyAccessEntryId, passcode) {
    assertIdPart(partyAccessEntryId, "the access entry id");
    assertPartyKeyedCode(passcode, "a passcode");
    return keyedDigest(pepper, `party-passcode\u0000${partyAccessEntryId}\u0000${passcode}`);
}
//# sourceMappingURL=keyed-code.js.map